import { deleteSite, siteList, sitePage } from "api/site";
import { SitePageRespData } from "api/types/site";
import { CONST } from "models/constants";
import { MenuRoutePaths } from "models/constants/routes";
import defineClTablecolumns, {
  ClassPageTableModel,
} from "models/tables/classPage";
import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { setLoading, setSiteList } from "store/appSlice";
import {
  setDevicePageFilters,
  setSitesPageFilters,
  SitesPageFilters,
} from "store/page/pageSlice";

import AddSiteDialog from "widgets/dialogs/addSite";
import GenericButton from "widgets/controls/buttons/generic";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";
import { useTranslation } from "react-i18next";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import { getSitesListFilters } from "store/page/pageSelectors";

const Sites = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useNotificationDialog();

  const [dataSource, setDataSource] = useState<SitePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState(false);

  // Get initial filters from store once
  const initialFilters = useSelector(getSitesListFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<SitesPageFilters>(
    () => initialFilters
  );

  const handleRowClick = (row: ClassPageTableModel) => {
    dispatch(
      setDevicePageFilters({
        paginationState: { pageIndex: 0, pageSize: 10 },
        sortingState: [{ desc: true, id: "name" }],
        totCount: 0,
        pageCount: 0,
        classId: row.id,
        model: "",
        status: undefined,
        installed: undefined,
      })
    );
    navigate(`/admin${MenuRoutePaths.DEVICES}`);
  };

  const columns = defineClTablecolumns(
    (info) => {
      const row = info.row.original;
      navigate(`/admin/sites/site?id=${row.id}`);
    },
    (info) => {
      const row = info.row.original;

      notify({
        header: t("notification.site.delete.title"),
        message: t("notification.site.delete.description"),
        okText: t("button.action.delete"),
        cancelText: t("button.action.cancel"),
        onOk: () => doDelete(row.id),
        onCancel: () => {},
      });
    }
  );

  const updatePage = () => {
    setDialog(false);
    setFilters((prevFilters) => ({
      ...prevFilters,
      paginationState: {
        pageIndex: 0,
        pageSize: 10,
      },
      totCount: 0,
      pageCount: 0,
    }));
    fetchData();
    updateSitelist();
  };

  const fetchData = useCallback(async () => {
    const { paginationState, sortingState, search } = filters;
    setIsLoading(true);
    const { data, status, isLoading, error } = await sitePage({
      search: search,
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      sort: sortingState.length > 0 ? sortingState[0].id : "",
      desc: sortingState.length > 0 ? sortingState[0].desc : false,
    });
    setIsLoading(isLoading);
    if (status === 200 && !error) {
      setDataSource(data.classes || []);
      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.pageIndex - 1,
          pageSize: paginationState.pageSize,
        },
        totCount: data.totalCount,
        pageCount: data.totalPages,
      }));

      dispatch(
        setSitesPageFilters({
          paginationState: { pageIndex: 0, pageSize: 10 },
          sortingState: [{ desc: true, id: "name" }],
          totCount: 0,
          pageCount: 0,
          search: search,
        })
      );
    }
  }, [
    filters.search,
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
  ]);

  const doDelete = async (id: string) => {
    dispatch(setLoading(true));

    const { status } = await deleteSite(id);
    dispatch(setLoading(false));
    if (status === 200) {
      notifySimple(t("notification.site.deleted"), "success");
      updatePage();
    } else {
      notifySimple(t("notification.site.delete.error"), "error");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSitelist = useCallback(async () => {
    dispatch(setSiteList([]));
    const { data, status } = await siteList();

    if (status === 200) {
      const sortedData = data.sort((a: { name: string }, b: { name: any }) => {
        return a.name.localeCompare(b.name);
      });
      dispatch(setSiteList(sortedData));
      localStorage.setItem("siteList", JSON.stringify(sortedData));
    }
  }, []);

  return (
    <div>
      <AddSiteDialog
        isOpen={dialog}
        onClose={() => setDialog(false)}
        onSuccess={updatePage}
      />

      <Table
        filterbar={
          <div className="flex flex-col gap-y-3">
            <div className="flex justify-end">
              <GenericButton
                onClick={() => {
                  setDialog(true);
                }}
                type="button"
                text={t("button.action.add.site")}
                size="md"
                permissionRole={CONST.ROLE_OWNER}
                disabled={isLoading}
              />
            </div>

            <SearchInput
              onChange={(filters) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  search: filters,
                  paginationState: {
                    pageIndex: 0,
                    pageSize: prevFilters.paginationState.pageSize,
                  },
                }));
              }}
              type="text"
              value={filters.search}
              sizes="md"
              rounded="rounded-xl"
              color="secondary"
              disabled={isLoading}
            />
          </div>
        }
        loading={isLoading}
        data={dataSource}
        columns={columns}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "name", desc: false }],
            }));
          }
        }}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default Sites;
